import cn from "classnames";
import Image from "next/image";
import { useState, useEffect } from "react";

import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { VideoDialog } from "@/src/component/partial/VideoDialog";
import { VideoPlayer } from "@/src/component/partial/VideoPlayer";
import CMSLogic from "@/src/logic/CMSLogic";
import { StrapiComponentType, ComponentType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import {
  ButtonLinkModule,
  GapBreakpointModule,
  SectionModule,
  TextBreakpointModule,
  ImageBreakpointModule,
  ImageSizedModule,
} from "@/src/component/partial/__module__";

import { EventCaptureWall, HeroCards } from "./component";
import styles from "./Hero.module.scss";

interface HeroProps extends ComponentType.DynamicPageSectionComponentProps<StrapiComponentType.Section.Hero> {}

function Hero({
  lazy,
  section,
  slug,
  snapToGrid,
  bottomDrawerHeight,
  index,
  initialSlide,
  classes,
  searchParams,
  mobileHint,
  deviceWidthHint,
  quality,
  layout,
}: HeroProps) {
  const hero: StrapiComponentType.Section.Hero = section.type?.[0];
  const videoId = `${slug}.${section.sectionId}.hero.video`;
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [eventCaptureWall, setEventCaptureWall] = useState<boolean>(false);
  const [isSectionSeen, setIsSectionSeen] = useState(false);
  const TAKEOVER_INTERVAL_KEY = `HERO_TAKEOVER_INTERVAL_KEY_${hero.id}`;
  const keepTakeoverMounted = (typeof initialSlide === "number" || typeof initialSlide === "undefined") && (initialSlide ?? 0) === index;
  const hasCards = (hero.cards ?? []).length > 0;
  const topBgGradientOff = hero.backgroundGradientOff || hasCards;

  /**
   * prevent the event capture wall from trigger twice
   */
  useEffect(() => {
    //don't turn on the image takeover wall
    if (!hero.imageTakeoverOn) {
      return;
    }

    //check if it has been interval from last time seen
    if (typeof hero.imageTakeoverWaitMinutes === "number") {
      const value = localStorage.getItem(TAKEOVER_INTERVAL_KEY);
      if (value) {
        try {
          const lastTimeSeen = parseInt(value, 10);
          const millsecondsCoversion = hero.imageTakeoverWaitMinutes * 60 * 1000;
          const expireTime = millsecondsCoversion + lastTimeSeen;
          if (Date.now() < expireTime) {
            return;
          }
        } catch (err) {}
      }
    }

    //iniative the event capture wall
    if (typeof index === "number" && !isSectionSeen) {
      setIsSectionSeen(true);
      if (keepTakeoverMounted) {
        setEventCaptureWall(true);
      }
    }
  }, [keepTakeoverMounted, index, isSectionSeen, hero.imageTakeoverOn, hero.imageTakeoverWaitMinutes, hero.id, TAKEOVER_INTERVAL_KEY]);

  function textPosition(): string | undefined {
    switch (hero?.textPosition ?? "bottomLeft") {
      case "center":
        return styles.center;
      case "bottomCenter":
        return styles.bottomCenter;
      case "centerAndBottomCenter":
        return styles.centerAndBottomCenter;
      default:
        return;
    }
  }

  const handleSectionSeen = () => {
    if (!isSectionSeen) {
      setIsSectionSeen(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    handleSectionSeen();
  };

  const handleCloseDialogAndSkip = () => {
    document.getElementById("dynamic-page-next")?.click();
    setOpenDialog(false);
    handleSectionSeen();
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    handleSectionSeen();
    localStorage.setItem(TAKEOVER_INTERVAL_KEY, `${Date.now()}`);
  };

  const handleTurnOffEventWall = () => {
    setEventCaptureWall(false);
    setOpenDialog(true);
    handleSectionSeen();
    localStorage.setItem(TAKEOVER_INTERVAL_KEY, `${Date.now()}`);
  };

  return (
    <SectionModule
      section={section}
      slug={slug}
      classes={{
        section: classes?.section,
        styledContainer: cn(styles.hero, textPosition(), topBgGradientOff ? styles.gradientOff : undefined),
      }}
      stylePrefix="Hero"
      lazy={lazy}
      layout={layout}
      styleContainerId={hero.id}
      snapToGrid={snapToGrid}
      bottomDrawerHeight={bottomDrawerHeight}
      searchParams={searchParams}
      mobileHint={mobileHint}
      deviceWidthHint={deviceWidthHint}
      quality={quality}
      elevateHeaderGradient={(hero.cards ?? []).length > 0}
      disableBackgroundElements={CMSLogic.isImage(hero?.hero) || CMSLogic.isVideo(hero?.hero) || (hero.heroBreakpoints ?? []).length > 0}
      AfterChildComponent={
        <>
          <EventCaptureWall
            active={!!(hero.imageTakeoverOn && eventCaptureWall)}
            setActive={handleTurnOffEventWall}
          />
          <VideoPlayer
            lazy={lazy ?? "lazy"}
            asset={hero?.hero}
            videoId={videoId}
            classes={{
              container: styles.elevation,
              video: styles.video,
            }}
            poster={LoadFromCDN.imageSizeAndQuality(section.background?.asset?.data?.attributes?.url, 80, deviceWidthHint ?? 1920)}
          />
          {CMSLogic.isImage(hero?.hero) && (
            <ImgBgResponsive
              className={styles.poster}
              imageUrl={LoadFromCDN.imageSizeAndQuality(hero?.hero?.data?.attributes?.url, quality, 1920)}
              lazy={lazy}
              prefetch={lazy === "eager"}
            />
          )}
          {hero.heroBreakpoints && (
            <ImageBreakpointModule
              images={hero.heroBreakpoints}
              preload={true}
              classes={{
                container: styles.heroBreakpoints,
              }}
              id={hero.id}
              mobileHint={mobileHint}
              quality={quality}
              lazy={lazy}
            />
          )}
          {hero.imageTakeoverOn && (
            <IconButton
              className={styles.playIcon}
              onClick={handleOpenDialog}
            >
              <Image
                src={"/image/icon_play.svg"}
                alt="Play Icon"
                width={40}
                height={40}
                priority={true}
              />
            </IconButton>
          )}
        </>
      }
    >
      <GapBreakpointModule
        id={hero.id}
        gapBreakpoints={hero.gapBreakpoints}
        className={cn(styles.inner, textPosition())}
        inlineStyle={{
          paddingBottom:
            (hero.textPosition ?? "bottomLeft").includes("bottom") && bottomDrawerHeight ? `calc(${bottomDrawerHeight}px + 20px)` : undefined,
          maxWidth: typeof hero.textBoxWidth === "number" ? `${hero.textBoxWidth}px` : undefined,
          pointerEvents: hasCards ? "none" : undefined,
        }}
      >
        {(hero.highlightImages ?? []).map((opt) => (
          <ImageSizedModule
            key={opt.id}
            lazy={lazy}
            image={opt}
            stylePrefix="hero_image_sized"
            id={opt.id}
          />
        ))}
        <Box className={styles.titleContainer}>
          <TextBreakpointModule
            id={`hero_title_${hero.id}`}
            text={section.title}
            className={styles.title}
          />
          {hero.nextToTitle && (
            <TextBreakpointModule
              id={`hero_next_to_title_${hero.id}`}
              text={hero.nextToTitle}
              className={styles.nextToTitle}
            />
          )}
        </Box>
        {(hero.belowTitleText ?? []).map((text, i) => (
          <TextBreakpointModule
            key={i}
            id={`hero_below_title_text_${hero.id}_${i}`}
            text={text}
          />
        ))}
        <TextBreakpointModule
          id={`hero_desc_${hero.id}`}
          text={hero.description}
          className={styles.description}
        />
        {hero.cta && (
          <Box
            className={cn(styles.buttons, textPosition(), hero.stackCta ? styles.stackCta : undefined)}
            sx={{
              pointerEvents: hasCards ? "all" : undefined,
            }}
          >
            {(hero.cta ?? []).map((cta, i) => (
              <ButtonLinkModule
                key={i}
                buttonLink={cta}
              />
            ))}
          </Box>
        )}
      </GapBreakpointModule>
      {hero?.cards && (
        <HeroCards
          id={`Hero_HeroCards_${hero.id}`}
          cards={hero?.cards}
          backgroundGradientOff={hero.backgroundGradientOff}
          hero={hero}
          bottomDrawerHeight={bottomDrawerHeight}
          section={section}
          lazy={lazy}
        />
      )}
      {hero.imageTakeoverOn && (
        <Dialog
          keepMounted={keepTakeoverMounted}
          onClose={handleCloseDialog}
          open={openDialog}
        >
          <VideoDialog
            id={`Hero.VideoDialog.${hero.id}`}
            lazy={keepTakeoverMounted ? "eager" : "lazy"}
            handleCloseDialogAndSkip={handleCloseDialogAndSkip}
            handleCloseDialog={handleCloseDialog}
            heroLargeScreen={hero.imageTakeoverLargeScreen}
            heroSmallScreen={hero.imageTakeoverSmallScreen}
            heroPortraitSmallScreen={hero.imageTakeoverPortrait}
            openDialog={openDialog}
            eventCaptureWall={eventCaptureWall}
          />
        </Dialog>
      )}
    </SectionModule>
  );
}

export default Hero;
